import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, queryString } from '../api';
import { EmptyState, ErrorNotice, Field, Notice, Spinner, formatDate } from '../components/ui';
import type { CourseSummary, MemberListRow } from '../types';

interface CourseAttendee {
  member_id: number;
  first_name: string;
  last_name: string;
  town: string | null;
  completed_on: string | null;
  has_mentor: boolean;
}

interface CourseDetail extends CourseSummary {
  attendees: CourseAttendee[];
}

/**
 * Who came on the beginners course. This is where mentees come from, so a
 * course with nobody on it means the mentoring screen has nobody to offer.
 */
export function CoursesPage() {
  const [courses, setCourses] = useState<CourseSummary[] | null>(null);
  const [open, setOpen] = useState<CourseDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  function load() {
    api
      .get<{ courses: CourseSummary[] }>('/courses')
      .then((r) => setCourses(r.courses))
      .catch(setError);
  }

  useEffect(load, []);

  function openCourse(id: number) {
    api
      .get<{ course: CourseDetail }>(`/courses/${id}`)
      .then((r) => setOpen(r.course))
      .catch(setError);
  }

  return (
    <>
      <p className="muted small">
        <Link to="/mentoring">← Back to mentoring</Link>
      </p>

      <div className="page-head">
        <h1>Courses</h1>
        {!creating ? (
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            New course
          </button>
        ) : null}
      </div>

      {flash ? <Notice kind="success">{flash}</Notice> : null}
      <ErrorNotice error={error} />

      {creating ? (
        <NewCourseForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      ) : null}

      {courses === null && error === null ? <Spinner label="Loading courses" /> : null}
      {courses !== null && courses.length === 0 ? (
        <EmptyState title="No courses yet">
          Add the beginners course, then tick off who came.
        </EmptyState>
      ) : null}

      {courses !== null && courses.length > 0 ? (
        <table className="members">
          <thead>
            <tr>
              <th scope="col">Course</th>
              <th scope="col">Year</th>
              <th scope="col">Attendees</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {courses.map((course) => (
              <tr key={course.id}>
                <td data-label="Course">{course.name}</td>
                <td data-label="Year">{course.year}</td>
                <td data-label="Attendees">
                  {course.attendee_count}
                  {course.attendee_count > 0 ? (
                    <span className="muted block small">{course.completed_count} finished</span>
                  ) : null}
                </td>
                <td data-label="">
                  <button type="button" className="link" onClick={() => openCourse(course.id)}>
                    Who came
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {open !== null ? (
        <AttendanceEditor
          course={open}
          onClose={() => {
            setOpen(null);
            load();
          }}
          onChanged={(course, message) => {
            setOpen(course);
            setFlash(message);
            window.setTimeout(() => setFlash(null), 4000);
          }}
          onError={setError}
        />
      ) : null}
    </>
  );
}

function NewCourseForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [name, setName] = useState('Beginners Beekeeping Course');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/courses', { name, year: Number(year) });
      onCreated();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form wide" onSubmit={onSubmit}>
      <ErrorNotice error={error} />
      <div className="grid">
        <Field label="Course name" required value={name} onChange={(e) => setName(e.target.value)} />
        <Field label="Year" type="number" required value={year} onChange={(e) => setYear(e.target.value)} />
      </div>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create course'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function AttendanceEditor({
  course,
  onClose,
  onChanged,
  onError,
}: {
  course: CourseDetail;
  onClose: () => void;
  onChanged: (course: CourseDetail, message: string) => void;
  onError: (error: unknown) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberListRow[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .get<{ rows: MemberListRow[] }>(`/members${queryString({ q: query, per_page: 8 })}`)
        .then((r) => {
          if (!cancelled) setResults(r.rows);
        })
        .catch(() => undefined);
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  async function setAttendance(memberId: number, attending: boolean, completedOn?: string | null) {
    setBusy(true);
    try {
      const result = await api.post<{ course: CourseDetail }>(`/courses/${course.id}/attendance`, {
        member_id: memberId,
        attending,
        completed_on: completedOn ?? null,
      });
      onChanged(result.course, attending ? 'Added to the course.' : 'Removed from the course.');
      setQuery('');
      setResults([]);
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  const already = new Set(course.attendees.map((a) => a.member_id));

  return (
    <div className="card">
      <div className="page-head card-head">
        <h2>
          {course.name} {course.year}
        </h2>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="field search">
        <label htmlFor="attendee-search">Add somebody</label>
        <input
          id="attendee-search"
          type="search"
          placeholder="Search members by name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {results.filter((r) => !already.has(r.id)).length > 0 ? (
        <ul className="permission-list">
          {results
            .filter((r) => !already.has(r.id))
            .map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="link"
                  disabled={busy}
                  onClick={() => void setAttendance(row.id, true, null)}
                >
                  Add {row.first_name} {row.last_name}
                  {row.town ? <span className="muted"> · {row.town}</span> : null}
                </button>
              </li>
            ))}
        </ul>
      ) : null}

      {course.attendees.length === 0 ? (
        <EmptyState title="Nobody on this course yet" />
      ) : (
        <table className="members">
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Finished</th>
              <th scope="col">Mentor</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {course.attendees.map((attendee) => (
              <tr key={attendee.member_id}>
                <td data-label="Member">
                  <Link to={`/members/${attendee.member_id}`} className="member-name">
                    {attendee.first_name} {attendee.last_name}
                  </Link>
                  {attendee.town ? <span className="muted block small">{attendee.town}</span> : null}
                </td>
                <td data-label="Finished">
                  {attendee.completed_on ? (
                    formatDate(attendee.completed_on)
                  ) : (
                    <button
                      type="button"
                      className="link"
                      disabled={busy}
                      onClick={() =>
                        void setAttendance(attendee.member_id, true, new Date().toISOString().slice(0, 10))
                      }
                    >
                      Mark finished
                    </button>
                  )}
                </td>
                <td data-label="Mentor">
                  {attendee.has_mentor ? (
                    <span className="muted">paired up</span>
                  ) : (
                    <Link to="/mentoring">needs one</Link>
                  )}
                </td>
                <td data-label="">
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() => void setAttendance(attendee.member_id, false)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
